import axios from 'axios';

const csrfToken = document.querySelector('[name="csrf-token"]') || document.head.querySelector('meta[name="csrf-token"]') || { content: 'no-csrf-token' };

const transport = axios.create({
  withCredentials: true,
  credentials: 'include',
  headers: {
    common: {
      'X-CSRF-Token': csrfToken.content,
    },
  },
});

const url = window.location.href.includes('localhost') ? 'http://localhost:9112' : 'https://billing-api.superagi.com';
let BASE_PATH = process.env.NODE_ENV === 'development' ? url : 'https://billing-api.superagi.com';
if (process.env.NODE_ENV === 'staging') {
  BASE_PATH = 'https://billing-api-staging.superagi.com';
}

const salesUrl = 'http://localhost:3000';
let SALES_PATH = process.env.NODE_ENV === 'development' ? salesUrl : 'https://sales.superagi.com';
if (process.env.NODE_ENV === 'staging') {
  SALES_PATH = 'https://sales-staging.superagi.com';
}

const callPricingUrl = 'http://localhost:3000';
let CALL_PRICING_PATH = process.env.NODE_ENV === 'development' ? callPricingUrl : 'https://billing.superagi.com';
if (process.env.NODE_ENV === 'staging') {
  CALL_PRICING_PATH = 'https://billing-staging.superagi.com';
}

// Serializes Credits Usage filter opts to backend query params. Month is sent
// as 1-12 (JS Date.getMonth() is 0-11, caller should bump before passing).
const buildUsageParams = ({ year, month, authUserIds } = {}) => {
  const p = {};
  if (year) p.year = year;
  if (month) p.month = month;
  if (authUserIds && authUserIds.length) p.auth_user_ids = authUserIds.join(',');
  return p;
};

export default {
  getBaseSalesPath() {
    return SALES_PATH;
  },
  async getBillingVersion(workspaceId = '') {
    try {
      let res = await transport.get(BASE_PATH + `/api/dashboard/version`, {
        params: {
          workspace_id: workspaceId,
        },
      });
      return res.data;
    } catch (error) {
      console.error('An error occurred while fetching workspace billing version: ', error);
      throw error;
    }
  },
  async checkUser(workspaceId = '') {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/user/me?workspace_id=${workspaceId}`);
      return response.data;
    } catch (error) {
      localStorage.clear();
      window.location.href = `${SALES_PATH}/auth_users?next=${encodeURIComponent(window.location.href)}`;
      return null;
    }
  },

  async getBillingOverview() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/overview`);
      return response.data;
    } catch (error) {
      console.error('Error fetching billing overview:', error);
      return null;
    }
  },

  async getBillingPlans() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/plans`);
      return response.data;
    } catch (error) {
      console.error('Error fetching billing plans:', error);
      return null;
    }
  },

  async getWorkspacePlans() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/workspace_plans`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace plans:', error);
      return null;
    }
  },

  async getCustomerPortal() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/customer_portal`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer portal link:', error);
      return null;
    }
  },

  async getOrCreateAccount() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/get_or_create_account`);
      return response.data;
    } catch (error) {
      console.error('Error getting or creating account:', error);
      return null;
    }
  },

  async getV2Pricing() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v2/pricing`);
      return response.data;
    } catch (error) {
      console.error('Error fetching v2 pricing:', error);
      return null;
    }
  },

  async updateV2SeatSubscription(payload) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/update_seat_subscription`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating v2 seat subscription:', error);
      return error?.response?.data || null;
    }
  },

  async createV2StripeSubscription(payload) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/stripe_v2_subscription`, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating v2 stripe subscription:', error);
      return error?.response?.data || null;
    }
  },

  async createV2RazorpaySubscription(payload) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/razorpay_seat_subscription`, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating v2 razorpay subscription:', error);
      return error?.response?.data || null;
    }
  },

  async calculateDue(planId, workspacePlanId, humanSeats, digitalSeats, billingPeriod = 'monthly', cancelToken = null, creditAddons = null, planTier) {
    try {
      const params = {
        plan_id: planId,
        workspace_plan_id: workspacePlanId,
        human_seats: humanSeats,
        digital_seats: digitalSeats,
        billing_period: billingPeriod,
        plan_tier: planTier,
      };

      if (creditAddons) {
        params.credit_addons = creditAddons.credit_addons;
      }

      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/calculate_due`, {
        params,
        cancelToken,
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating due amount:', error);
      return null;
    }
  },

  async createCheckoutSession(planId, workspacePlanId, humanSeats, digitalSeats, billingPeriod = 'monthly', creditAddons = null, planType = null, uiMode = null, planTier) {
    try {
      const data = {
        plan_id: planId,
        workspace_plan_id: workspacePlanId,
        human_seats: humanSeats,
        digital_seats: digitalSeats,
        billing_period: billingPeriod,
        plan_tier: planTier,
      };

      if (creditAddons) {
        data.credit_addons = creditAddons.credit_addons;
      }

      if (planType) {
        data.plan_type = planType;
      }

      if (uiMode) {
        data.ui_mode = uiMode;
      }

      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/checkout_session`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  },

  async verifyCheckoutSession(sessionId) {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/checkout/success`, {
        params: { session_id: sessionId },
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying checkout session:', error);
      return null;
    }
  },

  async handleCancelledCheckout(sessionId) {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/checkout/cancel`, {
        params: { session_id: sessionId },
      });
      return response.data;
    } catch (error) {
      console.error('Error handling cancelled checkout:', error);
      return null;
    }
  },

  async getCreditsOverview() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/credits/overview`);
      return response.data;
    } catch (error) {
      console.error('Error fetching credits overview:', error);
      return null;
    }
  },

  async getAgentConsumptionHistory(opts = {}) {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/credits/consumption/agent_consumption/history`, {
        params: buildUsageParams(opts),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching agent consumption history:', error);
      return null;
    }
  },

  // Workspace users, fetched from super_sales. Used to populate the User filter
  // on the Credits Usage page. filter=active limits to non-deleted seats.
  // super_sales' get_users scopes results to current_workspace_id, which it
  // resolves from (in order) the X-Workspace-Id header, the workspace_id
  // query param, then the session cookie. The session cookie isn't shared
  // across the billing <-> sales subdomains, so we send the workspace
  // explicitly from billing_frontend's localStorage — otherwise we get an
  // empty users array.
  async fetchWorkspaceUsers(filter = 'active') {
    try {
      const workspaceId = localStorage.getItem('billing_workspace_id') || '';
      const response = await transport.get(`${SALES_PATH}/api/get_users`, {
        params: { filter, workspace_id: workspaceId },
        headers: workspaceId ? { 'X-Workspace-Id': workspaceId } : {},
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace users:', error);
      return null;
    }
  },

  async getSoftwareConsumption() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/credits/consumption/software_consumption`);
      return response.data;
    } catch (error) {
      console.error('Error fetching software consumption:', error);
      return null;
    }
  },

  async getSoftwareConsumptionHistory() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/credits/consumption/software_consumption/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching software consumption history:', error);
      return null;
    }
  },

  async getWorkspaceCurrent() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/workspace/current`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace current:', error);
      return null;
    }
  },

  async getWorkspaceByName(workspaceName) {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/workspace/name`, {
        params: { workspaceName: workspaceName },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace by name:', error);
      return null;
    }
  },

  async addSoftwareCredits(amount) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/credits/add`, {
        amount: amount,
      });
      return response.data;
    } catch (error) {
      console.error('Error adding software credits:', error);
      return null;
    }
  },

  async switchWorkspace(workspaceId) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/workspace/change/${workspaceId}`, {});
      return response.data;
    } catch (error) {
      console.error('Error switching workspace:', error);
      return null;
    }
  },

  async userCount() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/workspace/usercount`, {});
      return response.data;
    } catch (error) {
      console.error('Error counting user:', error);
      return null;
    }
  },

  async calculatePurchaseCredits(credits, creditType) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/calculate_purchase_credits`, {
        credits: credits,
        credit_type: creditType,
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating credits purchase:', error);
      return null;
    }
  },

  async purchaseCustomCredits(credits, creditType) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/purchase_custom_credits`, {
        credits: credits,
        credit_type: creditType,
      });
      return response.data;
    } catch (error) {
      console.error('Error purchasing credits:', error);
      return null;
    }
  },

  async logout() {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/user/me/logout`);
      localStorage.clear();
      return response.data;
    } catch (error) {
      console.error('Error logging out:', error);
      return null;
    }
  },

  async getCreditsSliderData(planId = null, workspacePlanId = null) {
    try {
      const params = {};

      if (workspacePlanId) {
        params.workspace_id = workspacePlanId;
      } else if (planId) {
        params.plan_id = planId;
      }

      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/credit_pack_prices`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching credits slider data:', error);
      return null;
    }
  },

  async changeFreeTrialToFreePlan(workspaceId) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/billing/change_to_free_plan`, {
        workspace_id: workspaceId,
      });
      return response.data;
    } catch (error) {
      console.error('Error changing free trial to free plan:', error);
      return null;
    }
  },

  async checkPaymentDue(workspaceId) {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/${workspaceId}/check_payment_due`);
      return response.data;
    } catch (error) {
      console.error('Error checking payment due status:', error);
      return null;
    }
  },

  async getEnabledBillingPeriods() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/enabled_billing_periods`);
      return response.data;
    } catch (error) {
      console.error('Error fetching enabled billing periods:', error);
      return null;
    }
  },

  async getEnabledTiers() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/billing/enabled_tiers`);
      return response.data;
    } catch (error) {
      console.error('Error fetching enabled tiers:', error);
      return null;
    }
  },

  async getCallPricing(countryCode) {
    try {
      const response = await transport.get(`${CALL_PRICING_PATH}/api/public/billing/pricing_inventory/${countryCode}?v4`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call pricing:', error);
      return null;
    }
  },

  async getCountryList() {
    try {
      const response = await transport.get(`${CALL_PRICING_PATH}/api/public/billing/country_list?v4`);
      return response.data;
    } catch (error) {
      console.error('Error fetching country list:', error);
      return null;
    }
  },
};
